import { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import dynamic from "next/dynamic";

import { showAppMessage } from "../../redux/actions/app";
import {
  fetchIosVersions,
  updateIosVersion,
  createIosVersion,
  setIosVersionModalParams,
} from "redux/actions/ios";

import { Button, Form, Input, InputNumber, Spin, Radio, Switch } from "antd";
import BaseModal from "./BaseModal";

import "react-markdown-editor-lite/lib/index.css";
import { formatDate } from "../../utils/app";

const markdownit = require("markdown-it");

const MdEditor = dynamic(() => import("react-markdown-editor-lite"), {
  ssr: false,
});

const Modal = ({
  token,
  isOpen,
  hideModal,
  showAppMessage,
  updateIosVersion,
  createIosVersion,
  fetchIosVersions,
  iosVersionModalParams,
  setIosVersionModalParams,
}: any) => {
  const mdParser = markdownit({
    html: false,
    linkify: false,
    typographer: false,
  });

  const [form] = Form.useForm();
  const [iosVersion, setIosVersion] = useState<any>({});
  const [rawReleaseNote, setRawReleaseNote] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [forceUpdate, setForceUpdate] = useState<boolean>(false);

  function handleEditorChange({ html, text }: any) {
    setRawReleaseNote(text);
    setIosVersion({
      ...iosVersion,
      release_note: html,
      raw_release_note: text,
    });
    form.setFieldValue("raw_release_note", text);
    form.validateFields(["raw_release_note"]);
  }

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (!rawReleaseNote) {
        showAppMessage("请输入更新说明", "error");
        return;
      }

      setSubmitting(true);
      const submitData = {
        ...values,
        raw_release_note: rawReleaseNote,
        release_note: iosVersion.release_note,
        force_update: forceUpdate,
        disabled,
      };

      const callback = () => {
        hideModal();
        fetchIosVersions(token, 1);
        setSubmitting(false);
        setIosVersionModalParams({
          action: "create",
          iosVersion: {},
        });
        form.resetFields();
        setRawReleaseNote("");
        setDisabled(false);
        setForceUpdate(false);
      };

      try {
        if (iosVersionModalParams.action === "create") {
          createIosVersion(token, { ios_version: submitData }, callback);
        } else {
          updateIosVersion(
            token,
            {
              ...submitData,
              id: iosVersion.id,
            },
            callback
          );
        }
      } catch (error) {
        setSubmitting(false);
        showAppMessage({
          message: error.message,
          type: "error",
        });
      }
    });
  };

  useEffect(() => {
    if (iosVersionModalParams && iosVersionModalParams.action === "update") {
      const version = iosVersionModalParams.iosVersion;
      setIosVersion(version);
      setDisabled(version.disabled);
      setForceUpdate(version.force_update);
      setRawReleaseNote(version.raw_release_note || "");

      form.setFieldsValue({
        version: version.version,
        build_number: version.build_number,
        raw_release_note: version.raw_release_note,
        disabled: version.disabled,
        force_update: version.force_update,
      });
    } else {
      form.resetFields();
      setIosVersion({});
      setRawReleaseNote("");
      setDisabled(false);
      setForceUpdate(false);
    }
  }, [iosVersionModalParams, form]);

  return (
    <BaseModal
      className="ios-version-modal"
      isOpen={isOpen}
      hideModal={() => {
        hideModal();
        setIosVersionModalParams({
          action: "create",
          iosVersion: {},
        });
        form.resetFields();
        setRawReleaseNote("");
        setDisabled(false);
        setForceUpdate(false);
      }}
      showLogo={false}
      title={
        iosVersionModalParams?.action === "create"
          ? "新增 iOS 版本"
          : "修改 iOS 版本"
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-20"
      >
        <Form.Item
          label="版本号"
          name="version"
          rules={[
            { required: true, message: "请输入版本号，例如: 1.0.0" },
            {
              pattern: /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/,
              message: "请输入正确的语义化版本号格式，例如: 1.0.0",
            },
          ]}
        >
          <Input placeholder="请输入版本号，例如: 1.0.0" />
        </Form.Item>

        <Form.Item
          label="Build 号"
          name="build_number"
          rules={[{ required: true, message: "请输入 Build 号" }]}
        >
          <InputNumber placeholder="例如: 1" />
        </Form.Item>

        <Form.Item label="强制更新">
          <Switch
            checked={forceUpdate}
            onChange={setForceUpdate}
            checkedChildren="是"
            unCheckedChildren="否"
          />
          <span className="ml-10 text-gray-500">
            {forceUpdate
              ? "用户必须更新到此版本或更高版本"
              : "用户可选择是否更新"}
          </span>
        </Form.Item>

        <Form.Item label="是否启用">
          <Radio.Group
            onChange={(e) => {
              setDisabled(e.target.value);
            }}
            value={disabled}
          >
            <Radio value={true}>禁用</Radio>
            <Radio value={false}>启用</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          className="w-full"
          name="raw_release_note"
          label="更新说明"
          rules={[{ required: true, message: "请输入更新说明" }]}
        >
          <MdEditor
            name="raw_release_note"
            value={rawReleaseNote}
            className="my-md-editor"
            style={{ height: "200px" }}
            onChange={handleEditorChange}
            renderHTML={(text) => mdParser.render(text)}
            config={{
              view: {
                menu: true,
                md: true,
                html: false,
              },
            }}
            plugins={["list-unordered", "list-ordered"]}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            disabled={submitting}
          >
            {submitting ? "保存中..." : "保存"}
          </Button>
        </Form.Item>
      </Form>
    </BaseModal>
  );
};

const mapStateToProps = (state: any) => ({
  token: state.user.token,
  iosVersionModalParams: state.ios.iosVersionModalParams,
});

const mapDispatchToProps = (dispatch: any) =>
  bindActionCreators(
    {
      showAppMessage,
      updateIosVersion,
      createIosVersion,
      fetchIosVersions,
      setIosVersionModalParams,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
